import { ExpandMore, ViewList } from '@mui/icons-material';
import { AccordionDetails, IconButton, Tooltip } from '@mui/material';
import { Accordion, Typography } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { noop } from 'shell/utils/noop';
import { headCellsCadastralData } from './constants';
import { CadastralVM } from 'shell/view-models/cadastral';
import Table from 'shell/components/shared/table';
import { PmiButton } from 'shell/components/shared/button';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { CadastralData } from 'shell/models/person';
import { DateTime } from 'luxon';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { FitersCadastralVM } from 'system/pages/toponymy/circulation-area/list/types';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import ResidentsListModal from '../../components/residents-list-modal';

interface CadastralAccordionComponentProps {
  cadastralList: Array<CadastralVM>;
  handleChange: (key: string, value: Array<CadastralVM>) => void;
  isEdit: boolean;
}

const CadastralAccordionComponent = ({
  cadastralList,
  handleChange,
  isEdit,
}: CadastralAccordionComponentProps) => {
  const [open, setOpen] = useState<boolean>(true);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const filterTable = useFilterTable<FitersCadastralVM>();
  const { userProfile } = useUserProfile();

  const lockUnlock = useHttpClient(
    `${__PERSON_API_URL__}/dati-catastali-bloccati/terna-catastale`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  const [openModal, setOpenModal] = useState<{ open: boolean; el: CadastralVM | null }>({
    open: false,
    el: null,
  });

  const list = useMemo(() => {
    return cadastralList.map((item) => {
      const lockUnlockButton =
        isEdit &&
        filterTable.values?.isCadastral &&
        userProfile?.datiLogin?.profile?.feature?.gestioneToponomastica === '2' ? (
          <PmiButton
            variant="contained"
            color="primary"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              let data;

              if (item.lock) {
                data = {
                  key: {
                    foglio: item.sheet || undefined,
                    particella: item.particle || undefined,
                    subalterno: item.subordinate || undefined,
                  },
                  codiceVia: item.el?.codiceVia || undefined,
                  numeroCivico: item.el?.numeroCivico || undefined,
                  esponenteCivico: item.el?.esponenteCivico || undefined,
                  numeroInterno: item.el?.numeroInterno || undefined,
                  esponenteInterno: item.el?.esponenteInterno || undefined,
                  sezione: item.el?.sezione || undefined,
                };
              } else {
                data = {
                  foglio: item.sheet || undefined,
                  particella: item.particle || undefined,
                  subalterno: item.subordinate || undefined,
                };
              }

              (
                lockUnlock({
                  url: item.lock ? '/blocco' : '/sblocco',
                  data,
                }) as ResponsePromise<HttpResponse<CadastralData>>
              ).then((response) => {
                const responseParsed = response?.data.payload;
                const dateStart = responseParsed?.dataInizio
                  ? DateTime.fromFormat(responseParsed?.dataInizio, 'yyyyMMdd').toFormat(
                      'dd/MM/yyyy'
                    )
                  : '';

                const dateEnd = responseParsed?.dataFine
                  ? DateTime.fromFormat(responseParsed?.dataFine, 'yyyyMMdd').toFormat('dd/MM/yyyy')
                  : '';

                handleChange(
                  'cadastralList',
                  cadastralList.map((item1) => {
                    return item1.id === item.id
                      ? {
                          ...item1,
                          startBlockDate: dateStart,
                          endBlockDate: dateEnd,
                          lock: !item1.lock,
                        }
                      : item1;
                  })
                );
              });
            }}
          >
            {!item.lock ? 'Sblocco' : 'Blocco'}
          </PmiButton>
        ) : undefined;

      const residentsListButton = true ? (
        <Tooltip arrow title="Elenco Residenti">
          <span>
            <IconButton
              aria-haspopup="true"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();

                setOpenModal({
                  open: true,
                  el: item,
                });
              }}
              style={{
                padding: '4px',
              }}
            >
              <ViewList />
            </IconButton>
          </span>
        </Tooltip>
      ) : undefined;

      return {
        ...item,
        action: (
          <>
            {lockUnlockButton}
            {residentsListButton}
          </>
        ),
      };
    });
  }, [
    cadastralList,
    handleChange,
    isEdit,
    filterTable.values?.isCadastral,
    setOpenModal,
    userProfile?.datiLogin?.profile?.feature?.gestioneToponomastica,
  ]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-dati-catastali-content"
        id="panel-dati-catastali-header"
        classes={{
          root: 'pmi-flex-row-reverse',
        }}
        expandIcon={<ExpandMore />}
      >
        <Typography>Dati Catastali</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Table
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newRows) => setRowsPerPage(newRows)}
          count={list?.length || 0}
          onSort={noop}
          head={headCellsCadastralData}
          page={page}
          orders={{}}
          rowsPerPage={rowsPerPage}
          body={list || []}
          hideButtons
          ariaLabel="Dati Catastali"
          noSort
        />
      </AccordionDetails>
      {openModal.open ? (
        <ResidentsListModal
          open={openModal.open}
          el={openModal.el}
          onClose={() => {
            setOpenModal({
              el: null,
              open: false,
            });
          }}
        />
      ) : undefined}
    </Accordion>
  );
};

CadastralAccordionComponent.displayName = 'Cadastral Component';

const CadastralAccordion = memo(CadastralAccordionComponent);

export default CadastralAccordion;
